<!-- @format -->

<template>
    <div class="gst-policy">
        <DataLoading
            v-if="loading"
            class="mt-5 mb-5 pt-5 pb-5" />
        <template v-else>
            <PrismicRichText
                :field="content.data.title"
                class="gst-policy__title" />
            <PrismicRichText
                :field="content.data.subtitle"
                class="gst-policy__subtitle" />
            <span> {{ $t('countryDropdownLabel') }} </span>
            <DropDown
                :value="selectedCountry"
                :clearable="false"
                :items="countryCodesList"
                @input="onInputDo" />
            <div class="slicezone">
                <SliceZone
                    v-if="!loading"
                    :slices="content.data.body"
                    :context="slicesContext"
                    :components="{
                        simple_content: SimpleContent,
                    }" />
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import DropDown from '@core/shared/components/input/DropDown';
import DataLoading from '@core/shared/components/loading/DataLoading.vue';
import { LogService } from '@core/services';
import tenantUrlsConstants from '@tenant/app/utils/constants/tenantUrls';
import { addCounterOnSlices } from '@tenant/app/utils/prismicUtils';
import SimpleContent from '@tenant/app/static/slices/SimpleContent.vue';
import { redirectToHome } from '@tenant/app/utils/tenantUrlslUtils';
import { normalizePolicyCountries } from '@tenant/app/data/response/normalizePolicyCountries';
import { validateCountryCode } from '@tenant/app/utils/policyCountriesUtils';
import prismicConstants from '@tenant/app/utils/constants/prismic';

const CUSTOM_TYPE_POLICY_COUNTRIES = prismicConstants.CUSTOM_TYPES.SINGLE.POLICY_COUNTRIES,
    CUSTOM_TYPE_STATIC_CONTENT = prismicConstants.CUSTOM_TYPES.REPEAT.STATIC_CONTENT;

export default {
    name: 'PolicyPrismic',
    components: {
        DataLoading,
        DropDown,
    },
    i18nOptions: {
        namespaces: '_common',
        keyPrefix: 'pages.policy',
    },
    data() {
        return {
            content: null,
            policyCountries: null,
            selectedCountry: null,
            SimpleContent,
            loading: true,
        };
    },
    computed: {
        ...mapState({
            language: (state) => state.appState.language,
        }),
        ...mapGetters({
            appLocale: 'appState/getLocale',
        }),
        slicesContext() {
            return {
                eventId: this.$route.params.id,
            };
        },
        countryCodesList() {
            return this.policyCountries?.options || [];
        },
        countryCodeFromRoute() {
            if (!this.policyCountries) return '';
            const countryCode =
                this.$route.query.countryCode && this.$route.query.countryCode.toLowerCase();
            return countryCode && validateCountryCode(countryCode)
                ? countryCode
                : this.policyCountries.default_country;
        },
    },
    watch: {
        countryCodeFromRoute(newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                this.initByCountryCode(newValue);
            }
        },
    },
    methods: {
        async fetchPolicyCountries() {
            try {
                const response = await this.$prismic.client.getSingle(CUSTOM_TYPE_POLICY_COUNTRIES);
                this.policyCountries = normalizePolicyCountries(response.data);
                this.setSelectedCountry(this.policyCountries.options[0]);
                this.initByCountryCode(this.countryCodeFromRoute);
            } catch (e) {
                redirectToHome();
            }
        },
        setSelectedCountry(country) {
            this.selectedCountry = country;
        },
        initByCountryCode(countryCode) {
            const country = this.countryCodesList.find((country) => country.value === countryCode);
            if (country?.uid) {
                this.setSelectedCountry(country);
                this.getPolicyContent();
            }
        },
        onInputDo(item) {
            if (item.uid) {
                this.$router.replace({
                    ...this.$route,
                    query: {
                        ...this.$route.query,
                        countryCode: item.value,
                    },
                });
            } else if (item.url) {
                const travelExperiencePolicyUrl =
                    this.getTravelExperiencePolicyUrl(item.value.toUpperCase(), this.language) ||
                    item.url;
                window.open(travelExperiencePolicyUrl, '_blank');
            }
        },
        getTravelExperiencePolicyUrl(country, language) {
            const countryUrl = tenantUrlsConstants.COUNTRIES[country];
            return (
                countryUrl?.travelExperiencePolicy[language] ||
                countryUrl?.travelExperiencePolicy[countryUrl?.defaultLanguage] ||
                null
            );
        },
        async getPolicyContent() {
            this.loading = true;
            this.content = await this.getContent(this.appLocale);
            if (!this.content) {
                // fallback to master language
                this.content = await this.getContent();
            }
            if (this.content) {
                this.content.data.body = addCounterOnSlices(this.content);
                this.$nextTick(() => {
                    this.scrollToHashAfterLoading();
                });
                this.loading = false;
            } else {
                redirectToHome();
            }
        },
        async getContent(lang) {
            const config = lang ? { lang } : {};
            const documentUID =
                this.selectedCountry?.uid || prismicConstants.DEFAULT_POLICY_COUNTRY_UID;

            try {
                return await this.$prismic.client.getByUID(
                    CUSTOM_TYPE_STATIC_CONTENT,
                    documentUID,
                    config
                );
            } catch (e) {
                LogService.log(e.message);
            }
        },
        scrollToHashAfterLoading() {
            const hash = window.location.hash;
            if (hash) {
                this.$router.replace({ ...this.$route, hash: '' });
                this.$router.replace({ ...this.$route, hash });
            }
        },
    },
    async created() {
        await this.fetchPolicyCountries();
    },
};
</script>

<style lang="scss">
@import '@scssMixins';
@import '@scssVariables';

$slice-light-background: #ffffff;
$slice-light-section-background: #ffffff;
$slice-light-section-secondary-background: #f3f5f7;
$slice-light-text-color: #181e25;
$slice-light-text-color-2: #50657c;
$slice-light-link-color: #0860c4;
$slice-light-button-cta-disabled-background: #50657c;
$slice-light-button-cta-disabled-color: #ffffff;
$slice-light-button-cta-color: #ffffff;
$slice-light-button-cta-background: #0860c4;
$slice-light-button-background: #50657c;
$slice-light-button-color: #181e25;
$slice-light-button-hover-background: #50657c;
$slice-light-button-hover-color: #181e25;
$slice-light-button-active-background: #50657c;
$slice-light-button-active-color: #181e25;
$slice-light-border: #50657c;
$slice-light-dropdown-border: #c2c2c2;
$slice-light-dropdown-row-hover: #f3f5f7;
$slice-light-dropdown-text-color: #50657c;
$slice-light-image-border: #ffffff;
$slice-light-location-vertical-line: #c2c2c2;
$slice-light-modal-button-background: #d6d6d6;

$slice-dark-background: #000000;
$slice-dark-section-background: #000000;
$slice-dark-text-color: #ffffff;
$slice-dark-link-color: #0860c4;
$slice-dark-button-background: #50657c;
$slice-dark-button-color: #181e25;
$slice-dark-button-hover-background: #50657c;
$slice-dark-button-hover-color: #181e25;
$slice-dark-button-active-background: #50657c;
$slice-dark-button-active-color: #181e25;
$slice-dark-border: #181e25;

.gst-policy {
    margin: auto !important;
    font-family: 'AmericanSans', sans-serif !important;
    max-width: 959px;

    section[data-gst-nav='true'] {
        scroll-margin-top: 65px;
    }

    .gst-policy__subtitle {
        p {
            color: theme-color('tertiary') !important;
            font-size: font-size('xxs');
        }
    }

    .gst-slice {
        display: flex;
        width: 100%;

        .gst-slice__content {
            display: flex;
            flex-direction: column;
        }

        .gst-slice__content--width-100 {
            width: 100%;
            padding-right: unset;
            padding-left: unset;
            max-width: unset;
        }

        .gst-slice__content--height-100 {
            height: 100%;
            padding-top: unset;
            padding-bottom: unset;
        }

        h1 {
            line-height: 48px;
            font-size: 40px;
            font-weight: 800;
            font-style: normal;
        }

        h2 {
            line-height: 24px;
            padding-bottom: 8px;
            margin-bottom: 32px;
            color: $slice-light-text-color;
            font-size: 20px;
            font-weight: 700;
            font-style: normal;
            border-bottom: 4px solid transparent;
        }

        h3 {
            line-height: 21px;
            color: $slice-light-text-color;
            font-size: 18px;
            font-weight: 600;
            font-style: normal;
        }

        h4 {
            line-height: 17px;
            color: $slice-light-text-color;
            font-size: 14px;
            font-weight: 400;
            font-style: normal;
        }

        h5 {
            line-height: 17px;
            color: $slice-light-text-color-2;
            font-size: 14px;
            font-weight: 400;
            font-style: normal;
        }

        h6 {
            line-height: 21px;
            margin-bottom: 16px !important;
            color: rgba(0, 0, 0, 0.87);
            font-size: 16px;
            font-weight: 700;
            font-style: normal;
        }

        p {
            line-height: 21px;
            margin-bottom: 0;
            font-size: 14px;
            font-weight: 400;
            font-style: normal;
            margin-block-end: 14px;
        }

        a {
            color: $slice-light-link-color;
            font-size: 14px;
            font-weight: 400;
        }

        strong {
            a {
                font-weight: bold;
            }
        }
    }
}
</style>
